import { Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, map, switchMap } from 'rxjs';

import { ICONS } from '@gfe/core/services/icons/icons.service';
import {
  CardsListComponent,
  CardsListComponentConfig
} from '@gfe/shared/components/cards-list/cards-list.component';
import {
  SearchConfig,
  ToolboxComponent,
  ToolboxSection
} from '@gfe/shared/components/toolbox/toolbox.component';

import { SolverService } from '@core/services/solvers/solvers.service';
import { selectDataspaceModel } from '@core/store/dataspace/dataspace.selectors';
import { foldForSearch } from '@core/utils/search-text.utils';
import { isNil } from '@core/utils/shared-utils';

@Component({
  selector: 'app-solvers-list',
  imports: [ToolboxComponent, CardsListComponent],
  templateUrl: './solvers-list.component.html',
  styles: ``
})
export class SolversListComponent {
  private readonly solversService = inject(SolverService);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  protected search = signal<string>('');

  private search$ = toObservable(this.search);
  private fetchSolvers = toSignal(
    combineLatest([
      this.store.select(selectDataspaceModel).pipe(filter((x) => !isNil(x))),
      this.search$
    ]).pipe(
      switchMap(([dataspace, search]) => {
        const solverList$ = this.solversService.getSolverList$(dataspace.id);
        if (search && search.trim().length > 0) {
          return solverList$.pipe(
            map((solvers) =>
              solvers.filter((solver) =>
                foldForSearch(solver.name).includes(foldForSearch(search))
              )
            )
          );
        } else {
          return solverList$;
        }
      })
    )
  );

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  protected searchPlaceholder = computed(() => {
    this.currentLang(); // Trigger reactivity when language changes
    return this.translate.instant('common.search_placeholder');
  });

  solversListConfig = computed<CardsListComponentConfig>(() => {
    const solvers = this.fetchSolvers();

    if (!solvers || solvers.length === 0) {
      return {
        noDataMessage: 'No solvers available',
        cards: []
      };
    }
    const cards = solvers.map((solver) => ({
      id: solver.name,
      title: solver.name,
      tooltip: solver.description || `Solver ${solver.name}`,
      iconClass: this.getIconForSolver(solver.solver),
      isPinned: true,
      route: solver.id.toString(),
      labels: {
        Variantes: solver.totalParams?.toString() ?? '0'
      }
    }));

    return {
      noDataMessage: 'No solvers available',
      cards: cards
    };
  });

  private getIconForSolver(solverType: string): string {
    const iconMapping: { [key: string]: string } = {
      Routing: ICONS.path,
      Packing: ICONS.package,
      Picking: ICONS.warehouse,
      Planning: ICONS.calendarBlank,
      'Yield Management': ICONS.chartLine,
      'Assembly/ Assembled Solvers': ICONS.stack
    };

    return iconMapping[solverType] || ICONS.gear;
  }

  toolboxSections = computed<ToolboxSection[]>(() => {
    return [];
  });

  searchCfg: SearchConfig = {
    active: true,
    type: 'simple',
    shortcut: true
  };
}
